import React, {
  useEffect,
  useMemo,
  useCallback,
  Fragment,
  useState,
} from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Input from "../../../../components/form/Input";
import Select from "../../../../components/form/select/Select";
import Checkbox from "../../../../components/form/Checkbox";
import Textarea from "../../../../components/form/Textarea";
import Radio from "../../../../components/form/Radio";
import DatePicker from "../../../../components/form/DatePicker";
import Button from "../../../../components/form/Button";
import Form from "../../../../components/form/Form";
import useForm from "../../../../hooks/useForm";
import { dynamicFormList } from "../../../../services/dynamicFormService";
import {
  dynamicContactAdd,
  enquiryAdd,
} from "../../../../services/enquiryService";
import { useOutletContext } from "react-router-dom";
import { lngDetect } from "../../../../helpers/lngDetect";
import { t } from "i18next";

// Constants
const FORM_TYPES = {
  GLOBAL: "global-enquiry",
};

const FIELD_TYPES = {
  INPUT: "input",
  TEXTAREA: "textarea",
  SELECT: "select",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  EMAIL: "email",
  DATE: "date",
};

const BASIC_FIELDS = ["firstName", "lastName", "email", "fullName"];

const COMMON_INPUT_CLASSES =
  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple";
const COMMON_LABEL_CLASSES = "!text-lg !text-deep-purple capitalize";
const ERROR_INPUT_CLASSES = "!border-red-500";

const ContactForm = ({
  onSuccess = () => {},
  onError = () => {},
  onClose = () => {},
  submitButtonLabel = "Send Message",
  title = "Contact Form",
  description = "Please fill out the fields below.",
  className = "",
  formType = "enquiry",
  organizationId = null,
  referenceId = null,
}) => {
  const { lang } = useSelector((state) => state?.langProvider);
  const { session } = useSelector((state) => state?.authProvider);
  const [formMetadata, setFormMetadata] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [validationRules, setValidationRules] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Memoized values
  const filters = useMemo(
    () => ({
      limit: 10,
      page: 1,
      sortQuery: "ordering",
    }),
    []
  );

  const sessionUser = useMemo(() => session?.user, [session?.user]);
  const { organizer } = useOutletContext();

  // Create validation rules from form fields
  const createValidationRules = useCallback((fields) => {
    const rules = {};

    fields.forEach((field) => {
      if (!field.name) return;

      const fieldRules = [];

      // Required validation
      if (field.required) {
        fieldRules.push({
          required: true,
          message: `${field.label} is required`,
          errorType: "required",
        });
      }

      // Email validation
      if (field.inputType === "email" || field.name === "email") {
        fieldRules.push({
          type: "email",
          message: "Please enter a valid email address",
          errorType: "validation",
        });
      }

      // Number validation
      if (field.inputType === "number" || field.type === "number") {
        const numberRule = {
          type: "number",
          message: "Please enter a valid number",
          errorType: "validation",
        };

        if (field.validation?.min !== undefined) {
          numberRule.min = field.validation.min;
        }
        if (field.validation?.max !== undefined) {
          numberRule.max = field.validation.max;
        }

        fieldRules.push(numberRule);
      }

      // URL validation
      if (field.inputType === "url" || field.type === "url") {
        fieldRules.push({
          type: "link",
          message: "Please enter a valid URL",
          errorType: "validation",
        });
      }

      // Custom validation from field metadata
      if (field.validation) {
        if (field.validation.pattern) {
          fieldRules.push({
            type: "custom",
            pattern: field.validation.pattern,
            message:
              field.validation.patternMessage ||
              `${field.label} format is invalid`,
            errorType: "validation",
          });
        }

        if (field.validation.minLength || field.validation.maxLength) {
          fieldRules.push({
            type: "range",
            min: field.validation.minLength,
            max: field.validation.maxLength,
            message: `${field.label} must be between ${
              field.validation.minLength || 0
            } and ${field.validation.maxLength || "unlimited"} characters`,
            errorType: "validation",
          });
        }
      }

      if (fieldRules.length > 0) {
        rules[field.name] = fieldRules;
      }
    });

    return rules;
  }, []);

  // Update validation rules when form fields change
  useEffect(() => {
    if (formFields.length > 0) {
      const rules = createValidationRules(formFields);
      setValidationRules(rules);
    }
  }, [formFields, createValidationRules]);

  // Initialize useForm with validation rules
  const {
    values,
    handleChange,
    handleSubmit,
    setFieldsValue,
    errors,
    formValidate,
    setErrors,
  } = useForm({
    initialState: {},
    validationRules,
  });

  // Utility functions
  const isTermsAndConditions = useCallback((field) => {
    const defaultValue = field.defaultValue || "";
    return (
      typeof defaultValue === "string" &&
      defaultValue.includes("<") &&
      defaultValue.includes(">") &&
      (defaultValue.includes("<p>") ||
        defaultValue.includes("<ul>") ||
        defaultValue.includes("<li>"))
    );
  }, []);

  const isEmailField = useCallback(
    (field) => {
      const fieldLabel =
        lngDetect(field.label?.text)?.toLowerCase() ||
        field.label?.toLowerCase() ||
        "";
      return fieldLabel.includes("email") || field.type === "email";
    },
    [lang]
  );

  const getDefaultValueFromSession = useCallback(
    (fieldName, fieldLabel) => {
      if (!sessionUser) return "";

      const label = fieldLabel.toLowerCase();

      if (label.includes("email")) return sessionUser.email || "";

      if (label.includes("first name") || fieldName === "firstName") {
        const fullName =
          sessionUser.firstName ||
          sessionUser.first_name ||
          sessionUser.name ||
          "";
        if (fullName.includes(" ")) {
          return fullName.trim().split(" ")[0];
        }
        return fullName;
      }

      if (label.includes("last name") || fieldName === "lastName") {
        const fullName =
          sessionUser.firstName ||
          sessionUser.first_name ||
          sessionUser.name ||
          "";
        if (fullName.includes(" ")) {
          const nameParts = fullName.trim().split(" ");
          return nameParts.slice(1).join(" ");
        }
        return sessionUser.lastName || sessionUser.last_name || "";
      }

      if (label.includes("full name") || fieldName === "fullName") {
        const firstName =
          sessionUser.firstName ||
          sessionUser.first_name ||
          sessionUser.name ||
          "";
        const lastName = sessionUser.lastName || sessionUser.last_name || "";

        if (firstName && lastName && !firstName.includes(" ")) {
          return `${firstName} ${lastName}`;
        }
        return firstName;
      }

      return "";
    },
    [sessionUser]
  );

  // API queries with error handling
  const fetchFormData = async (type, additionalParams = {}) => {
    const res = await dynamicFormList({ ...filters, ...additionalParams });
    if (res?.status !== 200) {
      toast.warning(res?.message);
    }
    return res?.docs;
  };

  const { data: globalData, isLoading } = useQuery({
    queryKey: ["global-contactForm", filters],
    queryFn: () =>
      fetchFormData(FORM_TYPES.GLOBAL, { type: FORM_TYPES.GLOBAL }),
  });

  // Field processing
  const createField = useCallback(
    (field, additionalProps = {}) => {
      const fieldLabel =
        lngDetect(field.label?.text) || field.label || "Untitled";
      const fieldPlaceholder =
        lngDetect(field.placeholder) || `Enter ${fieldLabel}`;

      return {
        id: field._id,
        key: field.key,
        label: fieldLabel,
        placeholder: fieldPlaceholder,
        type: field.type,
        required: field.required || false,
        defaultValue: field.defaultValue,
        validation: field.validation,
        options:
          field.options?.map((opt) => ({
            label: lngDetect(opt.label?.text) || opt.label || "",
            value: opt.key,
            default: opt.default || false,
          })) || [],
        ...additionalProps,
      };
    },
    [lang]
  );

  const processFormFields = useCallback(
    (formDataArray, isGlobal) => {
      if (!formDataArray?.data?.length) return [];

      const fields = [];

      formDataArray.data.forEach((form) => {
        form.sectionDetail?.forEach((section) => {
          const sortedFields = [...(section.relatedField || [])].sort(
            (a, b) => (a.ordering || 0) - (b.ordering || 0)
          );

          sortedFields.forEach((field) => {
            const fieldLabel =
              lngDetect(field.label?.text) || field.label || "Untitled";
            const isPhoneField =
              fieldLabel.toLowerCase().includes("phone") ||
              fieldLabel.toLowerCase().includes("mobile");
            const isTermsField = isTermsAndConditions(field);
            const isEmail = isEmailField(field);

            if (isEmail) {
              fields.push(
                createField(field, {
                  name: "email",
                  type: FIELD_TYPES.INPUT,
                  inputType: "email",
                  placeholder: "Enter your email address",
                  isGlobal,
                })
              );
            } else if (isPhoneField) {
              fields.push(
                createField(field, {
                  name: field.key,
                  type: FIELD_TYPES.INPUT,
                  inputType: "number",
                  placeholder: "Enter your phone number",
                  isGlobal,
                })
              );
            } else if (isTermsField) {
              fields.push(
                createField(field, {
                  name: field.key,
                  type: FIELD_TYPES.CHECKBOX,
                  htmlContent: field.defaultValue,
                  isTermsField: true,
                  isGlobal,
                })
              );
            } else {
              fields.push(
                createField(field, {
                  name: field.key,
                  inputType:
                    field.type === FIELD_TYPES.INPUT ? "text" : undefined,
                  isGlobal,
                })
              );
            }
          });
        });
      });

      return fields;
    },
    [lang, isTermsAndConditions, isEmailField, createField]
  );

  const processedFormFields = useMemo(() => {
    return processFormFields(globalData, true);
  }, [globalData, processFormFields]);

  // Form metadata
  const processedFormMetadata = useMemo(() => {
    const globalFields = [];

    processedFormFields.forEach((field) => {
      const fieldInfo = {
        fieldKey: field.name,
        fieldId: field.id,
        originalKey: field.key,
        isGlobal: field.isGlobal,
      };

      globalFields.push(fieldInfo);
    });

    return {
      globalFormId: globalData?.data?.[0]?._id,
      orgFormId: null,
      globalFields,
      orgFields: [],
    };
  }, [processedFormFields, globalData]);

  // Effects
  useEffect(() => {
    setFormMetadata(processedFormMetadata);
    setFormFields(processedFormFields);
  }, [processedFormMetadata, processedFormFields]);

  useEffect(() => {
    if (sessionUser && processedFormFields.length > 0 && setFieldsValue) {
      const sessionData = {};

      processedFormFields.forEach((field) => {
        const defaultValue = getDefaultValueFromSession(
          field.name,
          field.label
        );
        if (defaultValue && !values?.[field.name]) {
          sessionData[field.name] = defaultValue;
        }
      });

      if (Object.keys(sessionData).length > 0) {
        setFieldsValue(sessionData);
      }
    }
  }, [
    sessionUser,
    processedFormFields,
    setFieldsValue,
    getDefaultValueFromSession,
    values,
  ]);

  // Custom validation for complex field types (radio, checkbox)
  const validateComplexFields = useCallback(
    (formValues) => {
      const customErrors = {};

      processedFormFields.forEach((field) => {
        if (!field.required) return;

        const fieldValue = formValues[field.name];

        if (field.type === "radio") {
          if (!fieldValue || typeof fieldValue !== "object") {
            customErrors[field.name] = {
              message: `${field.label} is required`,
              type: "required",
            };
          } else {
            const hasSelectedValue = Object.values(fieldValue).some(
              (val) => val === true
            );
            if (!hasSelectedValue) {
              customErrors[field.name] = {
                message: `${field.label} is required`,
                type: "required",
              };
            }
          }
        } else if (field.type === "checkbox") {
          if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
            customErrors[field.name] = {
              message: `${field.label} is required`,
              type: "required",
            };
          } else {
            const hasCheckedValue = fieldValue.some(
              (item) =>
                typeof item === "object" &&
                Object.values(item).some((val) => val === true)
            );
            if (!hasCheckedValue) {
              customErrors[field.name] = {
                message: `${field.label} is required`,
                type: "required",
              };
            }
          }
        } else if (field.type === "select") {
          if (
            !fieldValue ||
            (typeof fieldValue === "string" && fieldValue.trim() === "")
          ) {
            customErrors[field.name] = {
              message: `Please select a ${field.label.toLowerCase()}`,
              type: "required",
            };
          }
        }
      });

      return customErrors;
    },
    [processedFormFields]
  );

  // Helper functions for form processing
  const processFieldValue = useCallback((fieldValue) => {
    if (
      fieldValue === "" ||
      fieldValue === null ||
      fieldValue === undefined ||
      fieldValue === false
    ) {
      return null;
    }

    if (
      typeof fieldValue === "object" &&
      !Array.isArray(fieldValue) &&
      fieldValue !== null
    ) {
      const selectedKey = Object.keys(fieldValue).find(
        (key) => fieldValue[key] === true
      );
      return selectedKey || null;
    }

    if (Array.isArray(fieldValue)) {
      const selectedValues = fieldValue
        .filter((item) => typeof item === "object")
        .map((item) => {
          const selectedKey = Object.keys(item).find(
            (key) => item[key] === true
          );
          return selectedKey;
        })
        .filter(Boolean);
      return selectedValues.length > 0 ? selectedValues : null;
    }

    if (fieldValue instanceof Date) {
      return fieldValue.toISOString();
    }

    return fieldValue;
  }, []);

  // NEW: Custom payload processing function
  const processSubmissionData = useCallback(
    (formValues) => {
      if (!formMetadata) {
        console.warn("Form metadata not available yet");
        return {};
      }

      // Create payload in the specified format
      const payload = {
        type: formType,
      };

      // Add organization and referenceId
      if (organizationId) {
        payload.organization = organizationId;
      }

      if (referenceId || sessionUser?.referenceId) {
        payload.referenceId = referenceId || sessionUser.referenceId;
      }

      // Collect name parts for combination
      let firstName = "";
      let lastName = "";

      // Map form values to the required payload structure
      Object.keys(formValues).forEach((fieldKey) => {
        const fieldValue = processFieldValue(formValues[fieldKey]);
        if (fieldValue === null || fieldValue === undefined) return;

        // Find the field metadata to get the label
        const fieldMetadata = processedFormFields.find(
          (field) => field.name === fieldKey
        );
        const fieldLabel = fieldMetadata?.label?.toLowerCase() || "";

        // Map based on field labels rather than field keys/IDs
        if (
          fieldLabel.includes("first name") ||
          fieldLabel.includes("firstname")
        ) {
          firstName = fieldValue;
        } else if (
          fieldLabel.includes("last name") ||
          fieldLabel.includes("lastname")
        ) {
          lastName = fieldValue;
        } else if (
          fieldLabel.includes("full name") ||
          fieldLabel.includes("name") ||
          fieldKey.toLowerCase() === "name"
        ) {
          payload.name = fieldValue;
        } else if (
          fieldLabel.includes("email") ||
          fieldKey.toLowerCase() === "email"
        ) {
          payload.email = fieldValue;
        } else if (
          fieldLabel.includes("message") ||
          fieldLabel.includes("description") ||
          fieldLabel.includes("comment") ||
          fieldLabel.includes("details") ||
          fieldKey.toLowerCase().includes("message")
        ) {
          payload.message = fieldValue;
        } else if (
          fieldLabel.includes("reason") ||
          fieldLabel.includes("subject") ||
          fieldLabel.includes("category") ||
          fieldKey.toLowerCase().includes("reason")
        ) {
          payload.reason = fieldValue;
        } else if (
          fieldLabel.includes("organization") ||
          fieldKey.toLowerCase().includes("organization")
        ) {
          payload.organization = fieldValue;
        } else {
          // Skip fields that are likely IDs or system fields
          // Only add meaningful field data
          if (
            !fieldKey.match(
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
            )
          ) {
            payload[fieldKey] = fieldValue;
          }
        }
      });

      // Combine firstName and lastName if both exist but no full name was set
      if (firstName && lastName && !payload.name) {
        payload.name = `${firstName} ${lastName}`;
      } else if (firstName && !payload.name) {
        payload.name = firstName;
      } else if (lastName && !payload.name) {
        payload.name = lastName;
      }

      return payload;
    },
    [
      formMetadata,
      processFieldValue,
      sessionUser,
      formType,
      organizationId,
      referenceId,
      processedFormFields,
    ]
  );

  // Custom validation function
  const validateFormData = useCallback(
    (formValues) => {
      setHasAttemptedSubmit(true);

      const complexErrors = validateComplexFields(formValues);
      const allErrors = { ...errors, ...complexErrors };

      if (Object.keys(complexErrors).length > 0) {
        setErrors((prevErrors) => ({ ...prevErrors, ...complexErrors }));
      }

      return Object.keys(allErrors).length === 0;
    },
    [errors, validateComplexFields, setErrors]
  );

  // Contact form submission mutation
  const contactMutationFn = useCallback(
    async (formValues) => {
      const isValid = validateFormData(formValues);

      if (!isValid) {
        const allErrors = { ...errors };
        const complexErrors = validateComplexFields(formValues);
        const combinedErrors = { ...allErrors, ...complexErrors };

        const errorMessages = Object.values(combinedErrors)
          .map((error) => error.message)
          .filter(Boolean);

        const uniqueErrors = [...new Set(errorMessages)];

        if (uniqueErrors.length === 1) {
          toast.error(uniqueErrors[0]);
        } else if (uniqueErrors.length <= 3) {
          uniqueErrors.forEach((error) => toast.error(error));
        } else {
          toast.error(
            `Please fix ${uniqueErrors.length} validation errors before submitting`
          );
        }

        const firstErrorField = Object.keys(combinedErrors)[0];
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
          }
        }

        throw new Error("Please fix the validation errors before submitting");
      }

      // Use the new payload structure
      const contactPayload = processSubmissionData(formValues);

      const res = await enquiryAdd({ ...contactPayload });

      if (res?.status !== 201) {
        throw new Error(res?.message || "Contact form submission failed");
      }

      return res;
    },
    [validateFormData, errors, validateComplexFields, processSubmissionData]
  );

  // Contact form mutation
  const { mutate: submitContactForm, isPending } = useMutation({
    mutationFn: contactMutationFn,
    onSuccess: (res) => {
      toast.success(res?.message || "Contact form submitted successfully!");
      onClose();
      onSuccess(res);

      // Reset form after successful submission
      setFieldsValue({});
      setHasAttemptedSubmit(false);
      setErrors({});
    },
    onError: (error) => {
      const errorMessage =
        error?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage);
      onError(error);
    },
  });

  // Select options mapping
  const selectOptionsMap = useMemo(() => {
    const optionsMap = {};
    processedFormFields.forEach((field) => {
      if (field.type === FIELD_TYPES.SELECT && field.options) {
        optionsMap[field.id] = field.options.map((opt) => ({
          name: opt.label,
          value: opt.value,
        }));
      }
    });
    return optionsMap;
  }, [processedFormFields]);

  // Helper function to get field validation info
  const getFieldValidation = useCallback(
    (fieldName) => {
      if (!formValidate)
        return { errorType: "", errorText: "", required: false };
      return formValidate(errors, fieldName);
    },
    [formValidate, errors]
  );

  // Helper function to check if field has errors
  const hasFieldError = useCallback(
    (fieldName) => {
      if (!hasAttemptedSubmit) return false;

      if (errors[fieldName]) return true;

      if (validateComplexFields) {
        const complexErrors = validateComplexFields(values);
        return !!complexErrors[fieldName];
      }

      return false;
    },
    [errors, validateComplexFields, values, hasAttemptedSubmit]
  );

  // Field rendering function
  const renderField = useCallback(
    (field) => {
      const fieldValue = values?.[field.name] || "";
      const hasError = hasFieldError(field.name);
      const validation = getFieldValidation(field.name);

      const commonProps = {
        key: field.id,
        label: t(field.label),
        labelClasses: COMMON_LABEL_CLASSES,
        value: fieldValue,
        onChange: handleChange,
        inputClasses: `${COMMON_INPUT_CLASSES} ${
          hasError ? ERROR_INPUT_CLASSES : ""
        }`,
        required: field.required,
      };

      const fieldRenderers = {
        [FIELD_TYPES.INPUT]: () => (
          <div>
            <Input
              {...commonProps}
              inputName={field.name}
              inputPlaceholder={t(field.placeholder)}
              inputType={field.inputType || "text"}
              maxLength={field?.validation?.max}
              min={field?.validation?.min}
              pattern={field?.validation?.pattern}
              {...validation}
              {...formValidate(errors, field.name)}
            />
          </div>
        ),

        [FIELD_TYPES.TEXTAREA]: () => (
          <div>
            <Textarea
              key={field.id}
              label={t(field.label)}
              labelClasses={COMMON_LABEL_CLASSES}
              inputName={field.name}
              inputPlaceholder={t(field.placeholder)}
              inputValue={fieldValue}
              onChange={handleChange}
              required={field.required}
              inputClasses={`${COMMON_INPUT_CLASSES} ${
                hasError ? ERROR_INPUT_CLASSES : "!h-20"
              }`}
              {...formValidate(errors, field.name)}
            />
          </div>
        ),

        [FIELD_TYPES.SELECT]: () => (
          <div>
            <Select
              key={field.id}
              labelClasses={COMMON_LABEL_CLASSES}
              inputClasses={`${COMMON_INPUT_CLASSES} ${
                hasError ? ERROR_INPUT_CLASSES : ""
              }`}
              label={field.label}
              placeholder={field.placeholder}
              required={field.required}
              dropdownData={selectOptionsMap[field.id] || []}
              selectedValue={fieldValue}
              onChange={(value) => {
                handleChange({ target: { name: field.name, value } });
              }}
              {...formValidate(errors, field.name)}
            />
          </div>
        ),

        [FIELD_TYPES.RADIO]: () => (
          <div key={field.id} className="space-y-2">
            <label className={`block ${COMMON_LABEL_CLASSES} font-medium`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div
              className={`flex items-center gap-4 ${
                hasError ? "border border-red-500 rounded p-2" : ""
              }`}
            >
              {field.options.map((option) => {
                const isChecked = values?.[field.name]?.[option.value] === true;
                return (
                  <div key={option.value} className="flex items-center">
                    <Radio
                      radioLabel={option.label}
                      radioName={field.name}
                      value={option.value}
                      isChecked={isChecked}
                      onChange={() => {
                        handleChange({
                          target: {
                            name: field.name,
                            value: { [option.value]: true },
                          },
                        });
                      }}
                      className="mr-2"
                    />
                  </div>
                );
              })}
            </div>
            {errors[field?.key]?.message && (
              <Fragment>
                <div className="text-xs text-amber-500 mt-1">
                  <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                  {errors[field?.key]?.message}
                </div>
              </Fragment>
            )}
          </div>
        ),

        [FIELD_TYPES.CHECKBOX]: () => (
          <div key={field.id} className="space-y-2">
            <label className={`block ${COMMON_LABEL_CLASSES} font-medium`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div
              className={`${
                field.isTermsField ? "space-y-2" : "flex items-center gap-4"
              } ${hasError ? "border border-red-500 rounded p-2" : ""}`}
            >
              {field.options.map((option) => {
                const isChecked = Array.isArray(values?.[field.name])
                  ? values[field.name].some(
                      (item) => item[option.value] === true
                    )
                  : false;

                return (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-2 ${
                      field.isTermsField ? "mb-1" : ""
                    }`}
                  >
                    <Checkbox
                      checkboxName={field.name}
                      checked={isChecked}
                      value={option.value}
                      onChange={(e) => {
                        const currentArray = Array.isArray(values?.[field.name])
                          ? values[field.name]
                          : [];
                        const newArray = e.target.checked
                          ? [...currentArray, { [option.value]: true }]
                          : currentArray.filter((item) => !item[option.value]);

                        handleChange({
                          target: { name: field.name, value: newArray },
                        });
                      }}
                      checkboxLabel={option.label}
                      checkboxClass="border-gray-300"
                      checkboxLableClass={
                        field.isTermsField ? "!text-sm !text-gray-600" : ""
                      }
                    />
                  </div>
                );
              })}
            </div>
            {errors[field?.key]?.message && (
              <Fragment>
                <div className="text-xs text-amber-500 mt-1">
                  <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                  {errors[field?.key]?.message}
                </div>
              </Fragment>
            )}
          </div>
        ),

        [FIELD_TYPES.DATE]: () => (
          <div>
            <DatePicker
              key={field.id}
              label={field.label}
              labelClasses={`${COMMON_LABEL_CLASSES} !mb-2`}
              placeholder={field.placeholder}
              required={field.required}
              inputClassName={`${COMMON_INPUT_CLASSES} ${
                hasError ? ERROR_INPUT_CLASSES : ""
              }`}
              onChange={(val) => {
                handleChange({
                  target: { name: field.name, value: val?.startDate },
                });
              }}
              value={{
                startDate: values?.[field.name] || fieldValue?.startDate,
                endDate: values?.[field.name] || fieldValue?.endDate,
              }}
              {...formValidate(errors, field.name)}
            />
          </div>
        ),
      };

      return fieldRenderers[field.type]
        ? fieldRenderers[field.type]()
        : fieldRenderers[FIELD_TYPES.INPUT]();
    },
    [
      values,
      handleChange,
      selectOptionsMap,
      hasFieldError,
      getFieldValidation,
      formValidate,
      errors,
    ]
  );

  const getColumnSpan = useCallback((field) => {
    const spanMap = {
      [FIELD_TYPES.TEXTAREA]: "col-span-12",
      [FIELD_TYPES.RADIO]: "col-span-12",
      [FIELD_TYPES.CHECKBOX]: "col-span-12",
    };

    if (spanMap[field.type]) return spanMap[field.type];
    if (field.type === FIELD_TYPES.INPUT && field.inputType === "email")
      return "col-span-12";
    return "col-span-6";
  }, []);

  // Check if form has errors (only show after submit attempt)
  const hasErrors = useMemo(() => {
    if (!hasAttemptedSubmit) return false;

    const complexErrors = validateComplexFields(values);
    return (
      Object.keys(errors).length > 0 || Object.keys(complexErrors).length > 0
    );
  }, [errors, validateComplexFields, values, hasAttemptedSubmit]);

  const errorCount = useMemo(() => {
    if (!hasAttemptedSubmit) return 0;

    const complexErrors = validateComplexFields(values);
    const allErrors = { ...errors, ...complexErrors };
    return Object.keys(allErrors).length;
  }, [errors, validateComplexFields, values, hasAttemptedSubmit]);

  if (isLoading) {
    return (
      <div className={`py-6 px-8 bg-white rounded-xl ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`py-6 px-8 bg-white rounded-xl ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-deep-purple mb-2">{t(title)}</h2>
        {description && (
          <p className="text-gray-600 text-sm">{t(description)}</p>
        )}
      </div>

      <Form onSubmit={submitContactForm} handleSubmit={handleSubmit}>
        {/* Form Fields */}
        <div className="grid grid-cols-12 gap-4 mb-6">
          {processedFormFields.map((field) => (
            <div key={field.id} className={getColumnSpan(field)}>
              {renderField(field)}
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            buttonType="submit"
            buttonLabel={
              hasErrors && hasAttemptedSubmit
                ? `Fix ${errorCount} error${
                    errorCount !== 1 ? "s" : ""
                  } to continue`
                : t(submitButtonLabel)
            }
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
            {...(hasErrors &&
              hasAttemptedSubmit && {
                isDisable: true,
              })}
            buttonClasses="px-8 py-3"
          />
        </div>
      </Form>
    </div>
  );
};

export default ContactForm;
